import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

interface ReceiptBlockProps {
    /** Detail rows to display */
    rows: ReadonlyArray<{ label: string; value: string }>;
    /** Summary row label — omit to hide divider + summary row entirely */
    summaryLabel?: string;
    /** Summary row value */
    summaryValue?: string;
    /** Stagger rows in with receipt-row-in animation (delay per row in ms) */
    staggerDelay?: number;
    /** Type scale for every row. Defaults to `body`. */
    textVariant?: "body" | "small";
    /** Extra className for the outer wrapper */
    className?: string;
    /** Inline style for the outer wrapper (for visibility transitions) */
    style?: React.CSSProperties;
}

/**
 * Receipt/detail rows with an optional bold summary row separated by a divider.
 * Supports staggered row-in animation via CSS keyframes (requires `receipt-row-in` keyframe).
 *
 * @version 1.0.0
 */
export function ReceiptBlock({
    rows,
    summaryLabel,
    summaryValue,
    staggerDelay,
    textVariant = "body",
    className,
    style
}: ReceiptBlockProps): React.ReactElement {
    const stagger = staggerDelay !== undefined;
    return (
        <div
            className={`flex flex-col gap-1 md:gap-2 px-1${className ? ` ${className}` : ""}`}
            style={style}
        >
            {rows.map((row, i) => (
                <div
                    key={row.label}
                    className="flex justify-between text-foreground-neutral"
                    style={
                        stagger
                            ? {
                                  animation: `receipt-row-in 300ms ease-out ${i * staggerDelay}ms both`
                              }
                            : undefined
                    }
                >
                    <Text variant={textVariant} render={<span />}>
                        {row.label}
                    </Text>
                    <Text
                        variant={textVariant}
                        render={<span />}
                        className="text-foreground-base"
                    >
                        {row.value}
                    </Text>
                </div>
            ))}
            {summaryLabel && (
                <>
                    <div
                        className="h-px bg-line-base"
                        style={
                            stagger
                                ? {
                                      animation: `receipt-row-in 300ms ease-out ${rows.length * staggerDelay}ms both`
                                  }
                                : undefined
                        }
                    />
                    <div
                        // Summary row uses the same row-level typography
                        // as the detail rows above — label in
                        // `text-foreground-neutral`, value in
                        // `text-foreground-base`. The divider above is
                        // what reads "this is the rollup row," not a
                        // larger font.
                        className="flex justify-between text-foreground-neutral"
                        style={
                            stagger
                                ? {
                                      animation: `receipt-row-in 300ms ease-out ${(rows.length + 1) * staggerDelay}ms both`
                                  }
                                : undefined
                        }
                    >
                        <Text variant={textVariant} render={<span />}>
                            {summaryLabel}
                        </Text>
                        <Text
                            variant={textVariant}
                            render={<span />}
                            className="text-foreground-base"
                        >
                            {summaryValue}
                        </Text>
                    </div>
                </>
            )}
        </div>
    );
}
